import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { MailLogEntry } from '@aviaconsole/shared';
import { capi, companyUrl } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useI18n } from '../i18n';
import { formatDate } from '../lib/format';
import { useToast } from '../components/Toast';
import { Button, Card, Chip, Empty, Loading, PageHeader, cap, mailKindLabel, mailStatusTone } from '../components/ui';

interface Resp { items: MailLogEntry[]; stats: { sent30d: number; deliveredPct: number; failed: number; queued: number } }

export function MailLog() {
  const { company } = useAuth();
  const { t, s, lang } = useI18n();
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['mail', company?.id], queryFn: () => capi<Resp>('/mail'), refetchInterval: 30_000 });
  const retry = useMutation({ mutationFn: (id: string | null) => capi(id ? `/mail/${id}/retry` : '/mail/retry-all', { method: 'POST' }), onSuccess: () => { qc.invalidateQueries({ queryKey: ['mail'] }); toast.push(t('done')); }, onError: (e: Error) => toast.push(e.message, 'error') });
  const d = q.data;
  return (
    <>
      <PageHeader crumb={t('mailCrumb')} title={t('mailTitle')} sub={t('mailSub')} actions={<Button variant="primary" icon="fas fa-rotate-right" onClick={() => retry.mutate(null)} disabled={retry.isPending}>{t('mailAction')}</Button>} />
      {!d ? <Loading /> : (
        <div className="stack">
          <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
            {[[t('sent30'), String(d.stats.sent30d)], [t('delivered'), `${d.stats.deliveredPct}%`], [t('failedBounced'), String(d.stats.failed)], [t('queued'), String(d.stats.queued)]].map(([k, v]) => (
              <Card key={k} className="pad" pad={false}><div style={{ padding: '0.9rem 1rem' }}><div className="label">{k}</div><div className="mono" style={{ fontSize: '1.35rem', fontWeight: 700, color: 'var(--heading)' }}>{v}</div></div></Card>
            ))}
          </div>
          <Card pad={false} className="table">
            <table>
              <thead><tr><th>{t('colSent')}</th><th>{t('colDocument')}</th><th>{t('colRecipient')}</th><th>{t('colAttachments')}</th><th>{t('colResult')}</th><th className="r">{t('colAction')}</th></tr></thead>
              <tbody>
                {d.items.length === 0 && <Empty />}
                {d.items.map((m) => (
                  <tr key={m.id}>
                    <td className="mono small secondary" style={{ whiteSpace: 'nowrap' }}>{formatDate(m.sentAt ?? m.createdAt, lang, true)}</td>
                    <td><div className="ref">{m.invoiceId ? <a href={`/invoices/${m.invoiceId}`}>{m.documentRef}</a> : m.ticketId ? <a href={`/tickets/${m.ticketId}`}>{m.documentRef}</a> : m.documentRef ?? '—'}</div><div className="sub">{s(mailKindLabel(m.kind))}</div></td>
                    <td className="small" style={{ overflowWrap: 'anywhere' }}>{m.toEmail}</td>
                    <td className="small secondary">{m.attachments.length ? m.attachments.map((a) => a.name.split('.').pop()?.toUpperCase()).join(' · ') : '—'}</td>
                    <td><Chip tone={mailStatusTone(m.status)}>{s(cap(m.status))}</Chip><div className="small muted" style={{ marginTop: '0.25rem', textWrap: 'pretty' }}>{m.detail}</div></td>
                    <td className="r">
                      <div className="row" style={{ justifyContent: 'flex-end' }}>
                        {m.sentAt && <a className="linkbtn small" href={companyUrl(`/mail/${m.id}/eml`)} target="_blank" rel="noreferrer" title=".eml"><i className="fas fa-envelope" /></a>}
                        {(m.status === 'failed' || m.status === 'bounced') && <button type="button" className="pill active" onClick={() => retry.mutate(m.id)}><i className="fas fa-rotate-right" /> {t('btnRetry')}</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      )}
    </>
  );
}
